import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Tabs, Card, Col, Descriptions, Row, Checkbox, Spin, Space, Tag, Tooltip } from 'antd';
import { CheckCircleOutlined, DownloadOutlined, PlusOutlined, CloudSyncOutlined, CheckOutlined } from '@ant-design/icons';
import useEventStore from '../../store/eventStore';
import DataTable from '../../components/DataTable/DataTable';
import AddStandModal from '../../components/Stands/AddStandModal';
import StandSectorFormModal from '../../components/StandSectors/StandSectorFormModal';
import { Stand } from '../../models/stands/stand';
import { StandSector } from '../../models/standSectors/standSector';
import useStandSectorStore from '../../store/standSectoreStore';
import DataFormModal from '../../components/DataForm/DataFormModal';
import { Product } from '../../models/products/products';
import { FormikValues } from 'formik';
import DataForm from '../../components/DataForm/DataForm';
import useStandStore from '../../store/standStore';
import { useProductStore } from '../../store/productStore';
import { useTranslation } from 'react-i18next';
import { Util } from '../../store/util/util';
import EventFormModal from '../../components/Events/EventFormModal';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { Customer } from '../../models/customers';
import useCustomerStore from '../../store/customerStore';
import ModalUtil from '../../store/core/modalUtil';
import DownloadReportModal from './DownloadReportModal';
import StandTypeSelectModal from '../../components/StandTypeSelectModal';
import AddOrderToStandModal from '../../components/Events/AddOrderToStandModal';
import { usePreloadEventData, useIsEventCached, useEventPendingSyncCount, useOnlineStatus } from '../../offlinestore/useOfflineSync';
import { useAuthStore } from '../../store/authUserStore';
import { UserRole } from '../../models/users';
import './EventDetails.css';

const EventDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { selectedItem: selectedEvent, isLoading: isLoadingEvent, fetchItemById: fetchEventById, setSelectedItem, fetchChecklistDataByEventId } = useEventStore();
  const { eventStands, fetchStandsByEventId, setSelectedItem: setSelectedStand, deleteItem: deleteStand } = useStandStore();
  const { sectorsByEventId, fetchStandSectorsByEventId, deleteItem: deleteStandSector } = useStandSectorStore();
  const { items: products, productsOfSelectedEvent, addProductToEvent, deleteProductFromEvent, fetchItems: fetchProducts, fetchProductsByEvent } = useProductStore();
  const { items: customers, customersOfSelectedEvent, addCustomerToEvent, deleteCustomerFromEvent, fetchItems: fetchCustomers, fetchCustomersByEvent } = useCustomerStore();
  const navigate = useNavigate();
  const eventId = id!;
  const { t } = useTranslation();
  const { currentUser } = useAuthStore();
  const isAdmin = currentUser?.role === UserRole.ADMIN;

  const [invoiceModal, setInvoiceModal] = useState<{ open: boolean; customer: Customer | null; loading: boolean }>({
    open: false, customer: null, loading: false,
  });
  const [issuedArticlesModal, setIssuedArticlesModal] = useState<{ open: boolean; customer: Customer | null; loading: boolean }>({
    open: false, customer: null, loading: false,
  });
  const { downloadInvoiceByCustomerId, downloadIssuedArticlesReportByCustomerId } = useEventStore();
  
  const { preloadEvent, isPreloading, progress } = usePreloadEventData();
  const { isCached, isChecking } = useIsEventCached(eventId);
  const pendingSyncCount = useEventPendingSyncCount(eventId);
  const isOnline = useOnlineStatus();

  useEffect(() => {
    const fetchEventData = async () => {
      await Promise.all([
        fetchEventById(id!),
        fetchChecklistDataByEventId(id!),
        fetchStandsByEventId(eventId, true),
        fetchStandSectorsByEventId(eventId, true),
        fetchProducts(),
        fetchCustomers(),
        fetchProductsByEvent(eventId, true),
        fetchCustomersByEvent(eventId, true),
      ]);
    };
    fetchEventData();
  }, [id, fetchEventById, fetchChecklistDataByEventId, eventId, fetchStandsByEventId, fetchStandSectorsByEventId, fetchProducts, fetchProductsByEvent, fetchCustomers, fetchCustomersByEvent]);

  const handleRowClick = (stand: Stand) => {
    navigate(`/stands/details/${stand.id}`);
  };

  const handleCacheEvent = async () => {
    if (selectedEvent) {
      await preloadEvent(eventId, selectedEvent.name);
    }
  };

  const handleAddSector = () => {
    ModalUtil.openModal(<StandSectorFormModal initialValues={null} eventId={eventId} />,
      t('stand.createStandSector'),
      "50%");
  };

  const handleEditSector = (standSectorId: string) => {
    const newStandSector = sectorsByEventId?.find((s) => s.id === standSectorId);

    if (newStandSector) {
      ModalUtil.openModal(<StandSectorFormModal initialValues={newStandSector} eventId={eventId} />,
        t('stand.editStandSector'),
        "50%");
    }
  };

  const handleAddStand = (customerId: string) => {
    ModalUtil.openModal(<AddStandModal onClose={ModalUtil.closeModal} eventId={eventId} customerId={customerId} />);
  };

  const handleDeleteStand = async (standId: string) => {
    await deleteStand(standId);
    fetchStandsByEventId(eventId, true);
  };

  const handleDeleteSector = async (sectorId: string) => {
    await deleteStandSector(sectorId);
    fetchStandSectorsByEventId(eventId, true);
  };

  const handleAddProductSubmit = async (values: FormikValues) => {
    await addProductToEvent(eventId, values.productId);
    fetchProductsByEvent(eventId, true);

    ModalUtil.openModal(<DataFormModal<{ productId: '' }>
      initialValues={{ productId: '' }}
      onSubmit={handleAddProductSubmit}
      fields={[
        {
          label: t('events.product'),
          name: 'productId',
          type: 'select',
          placeholder: t('events.placeholderProduct'),
          options: products?.map((product: any) => ({
            value: product.id ?? '',
            label: product.name,
          })),
        },
      ]}
      FormComponent={DataForm as any}
    />, t('events.addProduct'))
  };

  const handleEditStand = (id: string, customerId: string) => {
    const stand = eventStands?.find((s) => s.id === id);

    setSelectedStand(stand!);

    ModalUtil.openModal(<AddStandModal
      onClose={ModalUtil.closeModal}
      eventId={selectedEvent?.id!}
      initialValues={stand!}
      customerId={customerId}
    />, t('general.edit'), '500px')
  };

  const handleAddOrderToStand = () => {
    ModalUtil.openModal(
      <AddOrderToStandModal
        eventId={eventId}
        customers={customersOfSelectedEvent}
        stands={eventStands}
        onClose={ModalUtil.closeModal}
      />,
      t('stand.createDeliveryBill'),
      "80%"
    );
  };

  if (isLoadingEvent) {
    return <Spin>Loading...</Spin>;
  }

  const expandedRowRender = (customer: Customer) => {
    const filteredStandsData = eventStands.filter(x => x.customer?.id === customer.id);

    return <>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => handleAddStand(customer.id!)}
      >
        {t('events.addStand')}
      </Button>

      <DataTable
        columns={[
          { header: t('general.name'), accessor: 'name', type: 'text' },
          { header: t('stand.sector'), accessor: 'sector.name', type: 'text' },
          { header: t('stand.isDeliveryDone'), accessor: 'isDeliveryDone', type: 'boolean' },
        ]}
        data={filteredStandsData}
        onDelete={handleDeleteStand}
        onEdit={(id) => handleEditStand(id, customer.id!)}
        onRowClick={handleRowClick}
        hidePagination
      />
    </>
  }

  const tabItems = [
    {
      key: '1',
      label: t('events.stands'),
      children: (
        <>
          <DataTable<Stand>
            columns={[
              { header: t('stand.customer'), accessor: 'customer.companyName', type: 'text' },
              { header: t('customer.standName'), accessor: 'name', type: 'text' },
              { header: t('stand.sector'), accessor: 'sector.name', type: 'text' },
              { header: t('stand.isDeliveryDone'), accessor: 'isDeliveryDone', type: 'boolean' },
            ]}
            data={eventStands}
            onRowClick={handleRowClick}
            enableFrontendFiltering={true}
          />
        </>
      ),
    },
    {
      key: '2',
      label: t('general.standOperators'),
      children: (
        <>
          <DataTable<Customer>
            columns={[
              { 
                header: t('customer.companyName'), 
                accessor: 'companyName', 
                type: 'actions', 
                renderCell: (item: Customer) => {
                  return (
                    <a 
                      style={{ color: '#1890ff', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customers/${item.id}`);
                      }}
                    >
                      {item.companyName}
                    </a>
                  );
                }
              },
              { header: t('customer.email'), accessor: 'email', type: 'text' },
              { header: t('customer.phone'), accessor: 'phone', type: 'text' },
              {
                header: t('events.customerOnEvent'), accessor: '', type: 'actions', renderCell: (item: Customer) => {
                  return <Checkbox checked={Boolean(customersOfSelectedEvent?.find(x => x.id === item.id))}
                    onChange={async (e: CheckboxChangeEvent) => {
                      e.preventDefault();
                      if (e.target.checked) {
                        await addCustomerToEvent(eventId, item.id);
                      } else {
                        await deleteCustomerFromEvent(eventId, item.id);
                      }
                      fetchCustomersByEvent(eventId, true);
                    }} />
                }
              },
              {
                header: '', accessor: '', type: 'actions', renderCell: (item: Customer) => {
                  return navigator.onLine && customersOfSelectedEvent?.find(x => x.id === item.id) && (
                    <div className="stand-operator-actions">
                      <Button
                        type="primary"
                        key="downloadInvoice"
                        className="stand-operator-action-btn"
                        icon={<DownloadOutlined />}
                        onClick={() => setInvoiceModal({ open: true, customer: item, loading: false })}
                      >
                        {t('events.downloadInvoice')}
                      </Button>

                      <Button
                        type="primary"
                        key="downloadIssuedArticlesReport"
                        className="stand-operator-action-btn"
                        icon={<DownloadOutlined />}
                        onClick={() => setIssuedArticlesModal({ open: true, customer: item, loading: false })}
                      >
                        {t('events.downloadIssuedArticlesReport')}
                      </Button>
                    </div>
                  )
                }
              },
            ]}
            data={customers}
            defaultPageSize={100}
            enableFrontendFiltering={true}
            expandedRowRender={expandedRowRender}
          />
        </>
      ),
    },
    {
      key: '3',
      label: t('events.products'),
      children: (
        <>
          <DataTable<Product>
            columns={[
              {
                header: t('general.name'),
                accessor: 'name',
                type: 'actions',
                renderCell: (item: Product) => (
                  <a
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/products/${item.id}`);
                    }}
                  >
                    {item.name}
                  </a>
                )
              },
              { header: t('products.shortName'), accessor: 'shortName', type: 'text' },
              {
                header: t('events.productOnEvent'), accessor: '', type: 'actions', renderCell: (item: Product) => {
                  return <Checkbox checked={Boolean(productsOfSelectedEvent?.find(x => x.id === item.id))}
                    onChange={async (e: CheckboxChangeEvent) => {
                      if (e.target.checked) {
                        await addProductToEvent(eventId, item.id);
                      } else {
                        await deleteProductFromEvent(eventId, item.id);
                      }
                      fetchProductsByEvent(eventId, true);
                    }} />
                }
              },
            ]}
            data={products}
            enableFrontendFiltering={true}
            defaultPageSize={100}
            defaultOrderByColumn='productIndex'
          />
        </>
      ),
    },
    {
      key: '4',
      label: t('events.sectors'),
      children: (
        <>
          <Button type="primary" onClick={handleAddSector} style={{ marginBottom: 16 }}>
            {t('events.addStandSector')}
          </Button>
          <DataTable<StandSector>
            columns={[
              { header: t('general.name'), accessor: 'name', type: 'text' },
              { header: t('general.notes'), accessor: 'notes', type: 'text' },
            ]}
            data={sectorsByEventId}
            onEdit={handleEditSector}
            onDelete={handleDeleteSector}
            enableFrontendFiltering={true}
          />
        </>
      ),
    }
  ];

  if (!selectedEvent) {
    if (isLoadingEvent) {
      return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}><Spin size="large" /></div>;
    }
    return <div>{t('events.notFound')} {!navigator.onLine && <span style={{ color: 'orange' }}>({t('general.offline')} - {t('general.dataMayNotBeCached')})</span>}</div>;
  }

  const handleEdit = () => {
    setSelectedItem(selectedEvent);
    ModalUtil.openModal(<EventFormModal
      initialValues={selectedEvent}
    />,
      selectedEvent ? t('events.editEvent') : t('events.createEvent'),
      "80%");
  };

  const eventActionButtons = (
    <div className="event-details-actions-bar">
      <Button
        type='primary'
        key="openChecklist"
        icon={<CheckCircleOutlined />}
        onClick={() => navigate(`/events/${eventId}/checklist`)}
        disabled={!navigator.onLine}
      >
        {t('events.checkList')}
      </Button>
      <Button
        type="primary"
        key="createDeliveryBill"
        data-tour="open-delivery-modal"
        onClick={handleAddOrderToStand}
      >
        {t('stand.createDeliveryBill')}
      </Button>
      <Button
        type='primary'
        key="downloadIssuedArticlesReport"
        icon={<DownloadOutlined />}
        onClick={() => {
          ModalUtil.openModal(<DownloadReportModal downloadType={3} />, t('events.selectStandType'), "50%")
        }}
      >
        {t('events.downloadIssuedArticlesReportByCustomer')}
      </Button>
      <Button
        type='primary'
        key="downloadIssuedArticlesReportByProduct"
        icon={<DownloadOutlined />}
        onClick={() => {
          ModalUtil.openModal(<DownloadReportModal downloadType={4} />, t('events.selectStandType'), "50%")
        }}
      >
        {t('events.downloadIssuedArticlesReportByProduct')}
      </Button>
    </div>
  );
  const statusColor = selectedEvent.status?.toLowerCase() === 'pending' ? 'orange'
    : selectedEvent.status?.toLowerCase() === 'finished' ? 'green'
    : 'blue';
  const statusTag = <Tag color={statusColor}>{t(`events.${selectedEvent.status}`)}</Tag>;

  return (
    <>
      <StandTypeSelectModal
        open={invoiceModal.open}
        loading={invoiceModal.loading}
        onCancel={() => setIssuedArticlesModal({ open: false, customer: null, loading: false })}
        onConfirm={async (standType) => {
          setIssuedArticlesModal(m => ({ ...m, loading: true }));
          await downloadIssuedArticlesReportByCustomerId(issuedArticlesModal.customer!, selectedEvent?.id!, standType);
          setIssuedArticlesModal({ open: false, customer: null, loading: false });
        }}
      />
      <div data-tour="event-info">
      <Card
        title={
          <Space>
            {selectedEvent?.name}
            {isCached && (
              <Tooltip title={t('general.eventCached')}>
                <Tag color="green" icon={<CheckOutlined />}>
                  {t('general.offline')}
                </Tag>
              </Tooltip>
            )}
            {pendingSyncCount > 0 && (
              <Tooltip title={`${pendingSyncCount} ${t('general.pendingSync')}`}>
                <Tag color="orange" icon={<CloudSyncOutlined />}>
                  {pendingSyncCount}
                </Tag>
              </Tooltip>
            )}
            {isPreloading && (
              <Tag color="blue" icon={<Spin size="small" />}>
                {progress}
              </Tag>
            )}
          </Space>
        }
        style={{ marginBottom: '20px' }}
        extra={
          <Space>
            {/* {isOnline && !isCached && !isPreloading && (
              <Tooltip title={t('general.cachingEvent')}>
                <Button 
                  icon={<CloudDownloadOutlined />} 
                  onClick={handleCacheEvent}
                  loading={isPreloading}
                >
                  {t('general.offline')}
                </Button>
              </Tooltip>
            )} */}
            {isAdmin && (
              <Button type="primary" onClick={handleEdit}>
                {t('general.edit')}
              </Button>
            )}
          </Space>
        }
      >
        <Row gutter={24} className="tablet">
          <Col span={24}>
            <Descriptions bordered column={1}>
              <Descriptions.Item label={t('events.status')}>{statusTag}</Descriptions.Item>
              <Descriptions.Item label={t('events.startDate')}>{Util.formatDate(selectedEvent.startDate)}</Descriptions.Item>
              <Descriptions.Item label={t('events.endDate')}>{Util.formatDate(selectedEvent.endDate)}</Descriptions.Item>
              <Descriptions.Item label={t('events.eventArea')}>{selectedEvent.eventArea}</Descriptions.Item>
              <Descriptions.Item label={t('events.description')}>{selectedEvent.description}</Descriptions.Item>
              <Descriptions.Item label={t('events.depositPrice')}>{selectedEvent.depositPrice} €</Descriptions.Item>
              <Descriptions.Item label={t('events.depositPriceDishes')}>{selectedEvent.depositPriceDishes} €</Descriptions.Item>
              <Descriptions.Item label={t('customer.city')}>{selectedEvent.address?.city}</Descriptions.Item>
              <Descriptions.Item label={t('customer.street')}>{selectedEvent.address?.street}</Descriptions.Item>
              <Descriptions.Item label={t('customer.postalCode')}>{selectedEvent.address?.postalCode}</Descriptions.Item>
              <Descriptions.Item label={t('customer.state')}>{selectedEvent.address?.state}</Descriptions.Item>
              <Descriptions.Item label={t('customer.country')}>{selectedEvent.address?.country?.name}</Descriptions.Item>
            </Descriptions>
            <div className="event-details-actions" data-tour="event-actions">
              {eventActionButtons}
            </div>
          </Col>
        </Row>
        <Row gutter={16} className="desktop">
          <Col span={12}>
            <Descriptions bordered column={1}>
              <Descriptions.Item label={t('events.status')}>{statusTag}</Descriptions.Item>
              <Descriptions.Item label={t('events.startDate')}>{Util.formatDate(selectedEvent.startDate)}</Descriptions.Item>
              <Descriptions.Item label={t('events.endDate')}>{Util.formatDate(selectedEvent.endDate)}</Descriptions.Item>
              <Descriptions.Item label={t('events.eventArea')}>{selectedEvent.eventArea}</Descriptions.Item>
              <Descriptions.Item label={t('events.description')}>{selectedEvent.description}</Descriptions.Item>
              <Descriptions.Item label={t('events.depositPrice')}>{selectedEvent.depositPrice} €</Descriptions.Item>
              <Descriptions.Item label={t('events.depositPriceDishes')}>{selectedEvent.depositPriceDishes} €</Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions bordered column={1}>
              <Descriptions.Item label={t('customer.city')}>{selectedEvent.address?.city}</Descriptions.Item>
              <Descriptions.Item label={t('customer.street')}>{selectedEvent.address?.street}</Descriptions.Item>
              <Descriptions.Item label={t('customer.postalCode')}>{selectedEvent.address?.postalCode}</Descriptions.Item>
              <Descriptions.Item label={t('customer.state')}>{selectedEvent.address?.state}</Descriptions.Item>
              <Descriptions.Item label={t('customer.country')}>{selectedEvent.address?.country?.name}</Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={24} data-tour="event-actions">
            <div className="event-details-actions">
              {eventActionButtons}
            </div>
          </Col>
        </Row>
      </Card>
      </div>

      <Tabs items={tabItems} defaultActiveKey="1" />
    </>
  );
};

export default EventDetails;
